<template>
  <div class="tennisracket-tool__container">
    <div v-if="!step" class="tennisracket-tool__placeholder" :style="placeholderStyle">
      <div class="tennisracket-tool__label">{{ findPerfectLabel }}</div>
      <div class="tennisracket-tool__button--start">
        <button type="button" class="button button--primary" @click="step = 1">
          {{ startToolLabel }}
        </button>
      </div>
    </div>
    <div v-else class="tennisracket-tool__selection">
      <div class="tennisracket-tool__steps_row">
        <div
          v-for="(stepDef, index) in steps"
          :key="stepDef.stepKey"
          class="tennisracket-tool__step"
          :class="{ 'tennisracket-tool__step--active': step > index }"
          @click="goTo(index + 1)"
        >
          {{ stepDef.stepLabel }}
        </div>
      </div>
      <div class="options_row">
        <div v-for="(stepDef, index) in steps" :key="stepDef.title">
          <template v-if="step === index + 1">
            <h3>{{ index + 1 }}. {{ stepDef.title }}</h3>
            <template v-if="stepDef.choices">
              <div
                v-for="choice in stepDef.choices"
                :key="choice.key"
                class="tennisracket-tool__choice_container"
                @click="
                  setValue(index + 1, stepDef.title, choice.title, choice.key, stepDef.attribute)
                "
              >
                <img class="tennisracket-tool__choice_image" :src="choice.image" alt="" />
                <span class="tennisracket-tool__choice_title">{{ choice.title }}</span>
              </div>
            </template>
            <template v-if="stepDef.input">
              <div class="tennisracket-tool__choice">
                <img
                  v-if="stepDef.input.image"
                  class="tennisracket-tool__choice_image"
                  :src="stepDef.input.image"
                  alt=""
                />
                <div class="tennisracket-tool__form">
                  <div class="form__row">
                    <div class="form__labelwrapper">
                      <label :for="`tennis-racket-${stepDef.attribute}`" class="form__label">
                        {{ stepDef.input.title }}
                      </label>
                    </div>
                    <div class="form__widgetwrapper">
                      <input
                        :id="`tennis-racket-${stepDef.attribute}`"
                        v-model="inputValues[stepDef.attribute ?? '']"
                        type="number"
                        class="form__input form__input--text"
                        :min="stepDef.input.min"
                        :max="stepDef.input.max"
                        required
                      />
                    </div>
                  </div>
                  <div v-if="stepDef.nextButton" class="tennisracket-tool__submit">
                    <button
                      type="button"
                      class="button button--secondary button--small button--collapsed"
                      @click="setInputValue(index + 1, stepDef.input.title, stepDef.attribute)"
                    >
                      {{ nextLabel }}
                    </button>
                  </div>
                </div>
              </div>
            </template>
            <template v-if="stepDef.summary">
              <div
                v-for="(selected, stepIndex) in selectedValues"
                :key="stepIndex"
                class="tennisracket-tool__summary-row"
              >
                <div class="tennisracket-tool__summary-key">{{ selected.label }}</div>
                <div class="tennisracket-tool__summary-value">
                  {{ selected.valueLabel || selected.value }}
                </div>
                <div class="tennisracket-tool__summary-back" @click="goTo(Number(stepIndex))">
                  {{ editLabel }}
                </div>
              </div>
              <div class="tennisracket-tool__button--results">
                <button
                  type="button"
                  class="button button--secondary"
                  :disabled="loading"
                  @click="showResults"
                >
                  {{ resultLabel }}
                </button>
              </div>
            </template>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import axios from 'axios';
import { computed, reactive, ref } from 'vue';

import { generateUrl } from '@shop/utils/Routing';
import { useTranslation } from '@shop/utils/Translation';

import type {
  TennisRacketSelectedValue,
  TennisRacketToolBlockProps,
} from './TennisRacketToolBlock.types';

const props = defineProps<TennisRacketToolBlockProps>();

const { t } = useTranslation('tennisracket_tool');

const placeholderStyle = computed(() =>
  props.backgroundImage ? { backgroundImage: `url('${props.backgroundImage}')` } : {}
);

const findPerfectLabel = computed(() => props.findPerfectLabel ?? t.value('racket.find_perfect'));
const startToolLabel = computed(() => props.startToolLabel ?? t.value('racket.start_tool'));
const nextLabel = computed(() => props.nextLabel ?? t.value('step.next'));
const editLabel = computed(() => props.editLabel ?? t.value('step.edit'));
const resultLabel = computed(() => props.resultLabel ?? t.value('step.result'));

const step = ref(0);
const loading = ref(false);
const inputValues = reactive<Record<string, string>>({});
const selectedValues = reactive<Record<number, TennisRacketSelectedValue>>({});

const setValue = (
  index: number,
  label: string,
  valueLabel: string,
  value: string,
  attribute?: string
): void => {
  selectedValues[index] = {
    label,
    valueLabel,
    value,
    attribute,
  };
  step.value += 1;
};

const setInputValue = (index: number, label: string, attribute?: string): void => {
  if (!attribute) {
    return;
  }
  selectedValues[index] = {
    label,
    attribute,
    value: inputValues[attribute] ?? '',
  };
  step.value += 1;
};

const goTo = (targetStep: number): void => {
  step.value = targetStep;
};

const showResults = async (): Promise<void> => {
  loading.value = true;

  const params: Record<string, string> = {
    url_path: window.location.pathname,
  };

  Object.values(selectedValues).forEach(selected => {
    if (selected.attribute) {
      params[selected.attribute] = selected.value;
    }
  });

  try {
    const result = await axios.get<{ url?: string }>(
      generateUrl('category_tool_tennisracket_url', params)
    );
    if (result.data.url) {
      window.location.href = result.data.url;
    }
  } finally {
    loading.value = false;
  }
};

const steps = props.steps;
</script>
